/**
 * Design Tokens - Auto-extracted from Figma
 * Source: Booking (Copy) - elgC8VKEx8QgNJTvjRVZDM
 * 
 * IMPORTANT: Always use these tokens instead of hardcoding values.
 * This ensures consistency with the Figma design system.
 */

// =============================================================================
// COLORS
// =============================================================================

import { colors as tenantColors, fontFamily as tenantFontFamily } from "../branding/generated/active-tokens";

const brandPalette = tenantColors.brand;

export const colors = {
    brand: brandPalette,
    
    // Neutral Colors
    neutral: {
      white: '#FFFFFF',
      lightest: '#EEEEEE',       // Dividers, borders, backgrounds
      lighter: '#CCCCCC',
      light: '#AAAAAA',          // Muted text, disabled states
      base: '#666666',           // Secondary text
      dark: '#444444',           // Body text (secondary)
      darker: '#222222',         // Body text (primary)
      darkest: '#000000',        // Titles, headings
    },
    
    // Semantic Colors (use these in components)
    text: {
      title: '#000000',          // Headings
      primary: '#222222',        // Main body text
      secondary: '#444444',      // Secondary body text
      muted: '#666666',          // Tertiary text
      disabled: '#AAAAAA',       // Disabled/placeholder text
    },
    
    background: {
      main: '#FFFFFF',
      secondary: '#EEEEEE',
      branded: brandPalette.primary,
    },
    
    border: {
      default: '#EEEEEE',
      dark: 'rgba(0, 0, 0, 0.15)',
    },
    
    button: {
      primary: brandPalette.primary,
      primaryHover: brandPalette.primaryDark1,
      secondary: '#FFFFFF',
    },

    link: brandPalette.primary,
    
    // Status Colors (commonly used alongside brand)
    status: {
      success: '#22C55E',        // Green (used in "Open" status, etc.)
      error: '#EF4444',
      warning: '#F59E0B',
      info: '#00B8FF',
    },
    
    // Opacity variants
    opacity: {
      white5: 'rgba(255, 255, 255, 0.05)',
      white10: 'rgba(255, 255, 255, 0.10)',
      white15: 'rgba(255, 255, 255, 0.15)',
      white20: 'rgba(255, 255, 255, 0.20)',
      white30: 'rgba(255, 255, 255, 0.30)',
      white40: 'rgba(255, 255, 255, 0.40)',
      white50: 'rgba(255, 255, 255, 0.50)',
      white60: 'rgba(255, 255, 255, 0.60)',
      black5: 'rgba(0, 0, 0, 0.05)',
      black10: 'rgba(0, 0, 0, 0.10)',
      black15: 'rgba(0, 0, 0, 0.15)',
      black20: 'rgba(0, 0, 0, 0.20)',
      black30: 'rgba(0, 0, 0, 0.30)',
      black40: 'rgba(0, 0, 0, 0.40)',
      black50: 'rgba(0, 0, 0, 0.50)',
      black60: 'rgba(0, 0, 0, 0.60)',
    },
  } as const;
  
  
  // =============================================================================
  // TYPOGRAPHY
  // =============================================================================
  
  export const typography = {
    // Font Families
    fontFamily: tenantFontFamily,
    
    // Font Weights
    fontWeight: {
      light: 300,
      regular: 400,
      medium: 500,
      semiBold: 600,
      bold: 700,
      extraBold: 800,
    },
    
    // Font Sizes (Desktop / Mobile)
    fontSize: {
      // Headings
      h1: { desktop: 56, mobile: 40 },
      h2: { desktop: 48, mobile: 36 },
      h3: { desktop: 40, mobile: 32 },
      h4: { desktop: 38, mobile: 28 },
      h5: { desktop: 28, mobile: 24 },
      h6: { desktop: 24, mobile: 22 },
      
      // Body Text
      textLarge: { desktop: 24, mobile: 20 },
      textMedium: { desktop: 22, mobile: 18 },
      textRegular: { desktop: 20, mobile: 18 },
      textSmall: { desktop: 18, mobile: 16 },
      textTiny: { desktop: 16, mobile: 14 },
      textExtraTiny: { desktop: 14, mobile: 14 },
    },
    
    // Line Heights
    lineHeight: {
      tight: 1.2,      // 120% - headings
      normal: 1.4,     // 140% - body text
      relaxed: 1.6,    // 160% - large text blocks
    },
  } as const;
  
  
  // =============================================================================
  // SPACING & SIZING
  // =============================================================================
  
  export const spacing = {
    // Base spacing scale (in pixels)
    0: 0,
    1: 4,
    2: 8,
    3: 12,
    4: 16,
    5: 20,
    6: 24,
    7: 28,
    8: 32,
    10: 40,
    12: 48,
    14: 56,
    16: 64,
    20: 80,
    24: 96,
    28: 112,
    
    // Section Padding (Desktop / Mobile)
    section: {
      small: { desktop: 48, mobile: 32 },
      medium: { desktop: 80, mobile: 48 },
      large: { desktop: 112, mobile: 64 },
    },
    
    // Page/Global Padding
    page: { desktop: 64, mobile: 20 },
  } as const;
  
  export const sizing = {
    // Container Widths
    container: {
      small: 768,
      medium: 1024,
      large: 1440,
    },
    
    // Max Widths
    maxWidth: {
      xxsmall: 320,
      xsmall: 400,
      small: 480,
      medium: 560,
      large: 768,
      xlarge: 1024,
      xxlarge: 1280,
    },
  } as const;
  
  
  // =============================================================================
  // BORDER RADIUS
  // =============================================================================
  
  export const radius = {
    none: 0,
    extraSmall: 4,
    small: 8,
    medium: 12,
    large: 16,
    full: 9999,
  } as const;
  
  
  // =============================================================================
  // SHADOWS
  // =============================================================================
  
  export const shadows = {
    // Elevation levels (smallest to largest)
    xs: '0 1px 2px rgba(0, 0, 0, 0.05)',
    
    sm: '0 1px 2px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.10)',
    
    md: '0 2px 4px rgba(0, 0, 0, 0.06), 0 4px 8px rgba(0, 0, 0, 0.10)',
    
    lg: '0 4px 6px rgba(0, 0, 0, 0.03), 0 12px 16px rgba(0, 0, 0, 0.08)',
    
    xl: '0 8px 8px rgba(0, 0, 0, 0.03), 0 20px 24px rgba(0, 0, 0, 0.08)',
    
    '2xl': '0 24px 48px -12px rgba(0, 0, 0, 0.18)',
    
    '3xl': '0 32px 64px -12px rgba(0, 0, 0, 0.14)',
    
    none: 'none',
  } as const;
  
  
  // =============================================================================
  // STROKE / BORDER
  // =============================================================================
  
  export const stroke = {
    borderWidth: 1,
    dividerWidth: 1,
  } as const;
  
  
  // =============================================================================
  // BREAKPOINTS
  // =============================================================================
  
  export const breakpoints = {
    mobile: 0,
    tablet: 768,
    desktop: 1024,
    wide: 1440,
  } as const;
  
  
  // =============================================================================
  // Z-INDEX
  // =============================================================================
  
  export const zIndex = {
    base: 0,
    dropdown: 100,
    sticky: 200,
    modal: 300,
    popover: 400,
    tooltip: 500,
  } as const;
  
  
  // =============================================================================
  // TRANSITIONS
  // =============================================================================
  
  export const transitions = {
    fast: '150ms ease',
    normal: '200ms ease',
    slow: '300ms ease',
  } as const;
  
  
  // =============================================================================
  // EXPORT ALL
  // =============================================================================
  
  export const tokens = {
    colors,
    typography,
    spacing,
    sizing,
    radius,
    shadows,
    stroke,
    breakpoints,
    zIndex,
    transitions,
  } as const;
  
  export default tokens;